<template>
  <div class="w-full" data-testid="prospects-list-detail">
    <div class="mt-6 flex justify-end">
      <RouterLink
        v-if="activeList"
        :to="{ name: 'editProspectList', params: { listId: route.params.listId } }"
        class="btn-outline-neutral-xs inline-flex items-center align-middle txt-body-s-bold text-main"
        data-testid="prospects-edit-list-link"
      >
        <SvgIcon :path="mdiPencilOutline" />
        {{ t('ProspectsCatalog.editList') }}</RouterLink
      >
    </div>
    <AlertBox v-if="activeList?.submittedAt" type="success" class="mt-5" data-testid="prospects-submitted-alert">
      <template #header>
        {{ t('Prospects.prospectSelectionSubmitted.title') }}
      </template>
      <div>
        {{ t('Prospects.prospectSelectionSubmitted.body') }}
      </div>
    </AlertBox>
    <AlertBox
      v-if="!activeList?.submittedAt && activeList?.event?.prospectSelectionUntil"
      type="warning"
      class="mt-5"
      data-testid="prospects-deadline-alert"
    >
      <template #header>
        {{ t('Prospects.submitByNotification.title', { date: formatDate(activeList.event.prospectSelectionUntil) }) }}
      </template>
      <div>
        {{ t('Prospects.submitByNotification.description', { date: formatDate(activeList.event.prospectSelectionUntil) }) }}
      </div>
    </AlertBox>

    <div v-if="prospectListItems.length">
      <TableLight class="mb-6 w-full" data-testid="prospects-list-table">
        <thead>
          <tr>
            <th class="text-start">{{ t('ProspectsCatalog.companyName') }}</th>
            <th class="text-start">{{ t('ProspectsCatalog.position') }}</th>
            <th class="text-start">{{ t('ProspectsCatalog.visitedEvents') }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <ProspectRow
            v-for="prospect in prospectListItems"
            :key="prospect.id"
            :facet-items="facetItems"
            :me="props.me"
            :prospect-list-id="activeList?.id"
            :prospect-lists="props.prospectLists"
            :prospect="prospect"
            @create-prospect-item="emit('createProspectItem', $event)"
            @delete-prospect-item="emit('deleteProspectItem', $event)"
          >
            <button
              v-if="activeList?.id && !activeList.submittedAt"
              class="h-6"
              :data-testid="`prospects-remove-item-${prospect.id}`"
              @click.stop="deleteProspectItem(prospect.id)"
            >
              <Trash2Icon class="lucide-icon-s text-icon-error" />
            </button>
          </ProspectRow>
        </tbody>
      </TableLight>
      <div class="mt-8">
        <div class="flex justify-end gap-2">
          <DropDown wrapper-class="w-full" data-testid="prospects-export-dropdown">
            <template #activator="{ open }">
              <button class="btn-secondary-color-s uppercase" data-testid="prospects-export-button" @click.prevent="open">
                {{ t('exportCta') }}
              </button>
            </template>
            <template #default>
              <ul class="m-0 list-none divide-y divide-solid divide-(--border-color-main) p-0">
                <li
                  v-for="format in exportFormats"
                  :key="`format-${format}`"
                  class="cursor-pointer px-2 py-2 text-left hover:bg-surface-elevation-2"
                >
                  <a :href="downloadUrl(format)" class="flex text-main visited:text-main">
                    {{ t(`exportFormats.${format}`) }}
                  </a>
                </li>
              </ul>
            </template>
          </DropDown>
          <RouterLink
            class="btn-secondary-color-s mb-8"
            :class="{ 'pointer-events-none opacity-50': !hasRequiredContactData }"
            :to="{ name: 'sendProspectListTest' }"
            data-testid="prospects-test-email-link"
            @click="preventNavigationWithoutContact"
          >
            {{ t('ProspectsCatalog.sendTestEmail') }}
          </RouterLink>
          <RouterLink
            v-if="!activeList?.submittedAt && !isEventEnded"
            class="btn-primary-color-s mb-8"
            :class="{ 'pointer-events-none opacity-50': !hasRequiredContactData }"
            :to="{ name: 'sendProspectListInvitations' }"
            data-testid="prospects-submit-list-link"
            @click="preventNavigationWithoutContact"
          >
            {{ t('ProspectsCatalog.submitProspectListCTA') }}
          </RouterLink>
        </div>
        <p v-if="!hasRequiredContactData" class="mt-2 text-right txt-body-s-regular text-subtle" data-testid="prospects-contact-hint">
          {{ t('ProspectsCatalog.missingContactDataHint') }}
        </p>
      </div>
    </div>
    <p v-else class="py-16 text-center" data-testid="prospects-list-empty-state">
      {{ t('ProspectsCatalog.emptyState') }}
    </p>
    <RouterView
      :active-list="activeList"
      :facet-items="props.facetItems"
      :me="props.me"
      :prospect-lists="props.prospectLists"
      @refetch-prospect-lists="emit('refetchProspectLists')"
    />
  </div>
</template>

<script setup lang="ts">
import { mdiPencilOutline } from '@mdi/js'
import { DropDown } from '@ramp106/omrjs-core-ui'
import { Trash2Icon } from '@ramp106/omrjs-core-ui/icons'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'

import type { ProspectFacetItems } from '@/components/Prospects/prospectFacetItems'
import type { MeQuery } from '@/gql/myomr'
import { toPlainId } from '@/helpers/globalIdHelper'
import { exportFormats, prospectListDownloadUrl, type exportFormat } from '@/services/ApiService'
import { TableLight } from '@/ui/TableLight'

import ProspectRow from './ProspectRow.vue'
import type { ProspectList } from './types'
const { t, d, locale } = useI18n()

function formatDate(date: string): string {
  return d(date, 'dateShort')
}
const route = useRoute()

const props = defineProps<{
  me: NonNullable<MeQuery['me']>
  facetItems: ProspectFacetItems
  prospectLists: ProspectList[]
}>()

const activeList = computed(() => props.prospectLists.find((prospectList) => toPlainId(prospectList.id) === route.params.listId))

const prospectListItems = computed(() => activeList.value?.prospects || [])

const isEventEnded = computed(() => {
  if (!activeList.value?.event?.endsAt) return false
  return new Date(activeList.value.event.endsAt) < new Date()
})

const hasRequiredContactData = computed(() => {
  const contactName = activeList.value?.contactName?.trim()
  const contactEmail = activeList.value?.contactEmail?.trim()
  return Boolean(contactName && contactEmail)
})

const emit = defineEmits<{
  (e: 'createProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'deleteProspectItem', value: { prospectId: string; prospectListId: string }): void
  (e: 'refetchProspectLists'): void
}>()

async function deleteProspectItem(prospectId: string) {
  if (!activeList.value) return
  emit('deleteProspectItem', { prospectId: prospectId, prospectListId: activeList.value.id })
}

function preventNavigationWithoutContact(event: Event) {
  if (!hasRequiredContactData.value) {
    event.preventDefault()
  }
}

const downloadUrl = computed(() => {
  const id = activeList.value?.id ? toPlainId(activeList.value.id) : null
  return (format: exportFormat) => (id ? prospectListDownloadUrl(id, locale.value, format) : '')
})
</script>
